import { RootState } from 'redux/store'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { BATCH_INTERVAL_MS } from 'constants.js'
import { LUA_ANIMATION_DURATION_MS } from 'redux/slices/liveUserActions'
import { User } from 'data/User'
import { createSelector } from 'reselect'
import moment from 'moment'
import { persistReducer } from 'redux-persist'
import { rand } from 'utils'
import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
import uuid from 'uuid/v4'

export interface Word {
    _id: string
    user: string
    message: string
    upvotes: string[]
}

export interface ScheduledWord extends Word {
    delay: number
    endsAt: number
    weight: number
    duration: number
}

export interface WordFlowBatch {
    wordFlowMessages: Word[]
}

export interface BatchReceivedPayload {
    batch: WordFlowBatch
    durationModifier: number
    minSpeed: number
    userId: string | null
}

export interface WordFlowState {
    words: ScheduledWord[]
    endDate: string | null
    messageLimitReached: boolean
    messagesSent: number
    showWarning: boolean
    shouldCalculateProgress: boolean
}

export const WORD_FLOW_ANIMATION_DURATION_MS = LUA_ANIMATION_DURATION_MS * 2

const WORD_FLOW_MESSAGE_LIMIT = 1

export const WORD_FLOW_DURATION_IN_SECONDS = 90

export const initialState: WordFlowState = {
    words: [],
    endDate: null,
    messageLimitReached: false,
    messagesSent: 0,
    showWarning: false,
    shouldCalculateProgress: false,
}

const wordsSlice = createSlice({
    name: 'words',
    initialState,
    reducers: {
        wordUpvoted: (state, { payload }: PayloadAction<{ word: ScheduledWord; user: User }>) => {
            const upvotedWord = state.words.find(word => word._id === payload.word._id)
            upvotedWord?.upvotes.push(payload.user._id)
        },
        wordAdded: (state, { payload }: PayloadAction<{ word: string; userId: string }>) => {
            state.messageLimitReached = state.messagesSent === WORD_FLOW_MESSAGE_LIMIT

            if (!state.messageLimitReached) {
                state.words.push({
                    message: payload.word,
                    _id: uuid(),
                    user: payload.userId,
                    delay: 0,
                    endsAt: Date.now() + WORD_FLOW_ANIMATION_DURATION_MS,
                    weight: 1,
                    duration: WORD_FLOW_ANIMATION_DURATION_MS,
                    upvotes: [],
                })
            }
        },
        increaseMessagesSent: state => {
            state.messagesSent++
            state.messageLimitReached = state.messagesSent === WORD_FLOW_MESSAGE_LIMIT
        },
        wordFlowBatchReceived: (state, { payload }: PayloadAction<BatchReceivedPayload>) => {
            const now = Date.now()
            const currentWords = state.words.filter(word => word.endsAt > now)
            const {
                durationModifier,
                minSpeed,
                batch: { wordFlowMessages },
                userId,
            } = payload

            const newWords = wordFlowMessages.filter(word => userId && word.user !== userId)

            const currentBatchSize = newWords.length
            const displayInterval = Math.max(BATCH_INTERVAL_MS / currentBatchSize, 700)

            for (
                let i = 0, delay = 0;
                i < currentBatchSize && delay < BATCH_INTERVAL_MS;
                i++, delay = displayInterval * i
            ) {
                const weight = rand()
                const speed = Math.max(weight, minSpeed)
                const duration = WORD_FLOW_ANIMATION_DURATION_MS * (speed + durationModifier)

                currentWords.push({
                    ...newWords[i],
                    upvotes: [],
                    endsAt: Date.now() + duration + delay,
                    duration,
                    delay,
                    weight,
                })
            }

            state.words = currentWords
        },
        activate: (state, { payload: endDate }: PayloadAction<string>) => {
            state.endDate = endDate
            state.showWarning = true
        },
        deactivate: state => {
            state.words = []
            state.endDate = null
            state.messageLimitReached = false
            state.messagesSent = 0
            state.showWarning = false
        },
        closeWarning: state => {
            state.showWarning = false
        },
        calculateProgress: state => {
            state.shouldCalculateProgress = true
        },
        stopCalculatingProgress: state => {
            state.shouldCalculateProgress = false
        },
    },
})

const { actions, reducer } = wordsSlice

export default persistReducer(
    {
        key: 'wordFlow',
        storage,
        blacklist: ['words', 'shouldCalculateProgress'],
    },
    reducer,
)

export { reducer }

export const {
    wordAdded,
    wordFlowBatchReceived,
    activate,
    deactivate,
    wordUpvoted,
    closeWarning,
    increaseMessagesSent,
    calculateProgress,
    stopCalculatingProgress,
} = actions

export const getWordFlowState = (store: RootState) => store.wordFlow

export const getWordFlow = createSelector(getWordFlowState, wordFlow => wordFlow.words)

export const getEndDate = createSelector(getWordFlowState, wordFlow => wordFlow.endDate)

export const getShouldCalculateProgress = createSelector(getWordFlowState, wordFlow => wordFlow.shouldCalculateProgress)

export const hasReachedMessageLimit = createSelector(getWordFlowState, wordFlow => wordFlow.messageLimitReached)

export const isWordFlowActive = createSelector(getEndDate, endDate => endDate && moment().isSameOrBefore(endDate))

export const isWarningVisible = createSelector(getWordFlowState, wordFlow => wordFlow.showWarning)

export const getTimeAndProgressLeft = createSelector(getShouldCalculateProgress, getEndDate, (_, endDate) => {
    let result = {
        timeLeft: 0,
        currentProgress: 0,
    }

    if (!endDate) return result

    const timeLeft = getTimeLeft(endDate)
    const secondsLeft = timeLeft / 1000

    result.currentProgress = 1 - secondsLeft / WORD_FLOW_DURATION_IN_SECONDS

    result.timeLeft = timeLeft

    return result
})

export const getTimeLeft = (endDate: string) => {
    const now = moment().toISOString()
    const timeLeft = moment(endDate).diff(now, 'milliseconds')
    return timeLeft
}
